// `useProperty(id)` — TanStack Query hook for /api/properties/:id.
//
// Single round-trip per DETAIL-02; reuses queryClient defaults
// (staleTime 30s) for StrictMode dev double-mount safety (Pitfall 14).
//
// 404 semantics: the underlying `fetchPropertyDetail` throws
// `PropertyNotFoundError` for 404 responses (and the sentinel
// `__amenity_refresh__` row, which Plan 06-01's helper rejects). The
// route shell discriminates on `error instanceof PropertyNotFoundError`
// to render the "Property not found" panel instead of the generic
// "Couldn't load this property" panel.
//
// Retry policy: never retry 404s (re-fetching a missing property is
// wasted bandwidth + jitters the page state). Other errors retry once
// per the global queryClient default (Plan 05-03).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PropertyDetailDTO } from '@/types/db';
import { fetchPropertyDetail, PropertyNotFoundError } from '@/lib/api';

export function useProperty(
  id: number | undefined,
): UseQueryResult<PropertyDetailDTO, Error> {
  return useQuery<PropertyDetailDTO, Error>({
    queryKey: ['property', id],
    queryFn: ({ signal }) => {
      if (id == null || !Number.isFinite(id)) {
        // Defensive — `enabled` should keep us from being called in this
        // state, but a paranoid guard is cheaper than diagnosing a bad
        // URL parse later.
        return Promise.reject(new PropertyNotFoundError(-1));
      }
      return fetchPropertyDetail(id, signal);
    },
    enabled: id != null && Number.isFinite(id),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof PropertyNotFoundError) return false;
      return failureCount < 1; // matches global retry: 1
    },
  });
}
